import React, { useState, useEffect, useCallback } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  Calendar, 
  CalendarDays,
  ExternalLink,
  RotateCcw,
  FileText,
  Save,
} from 'lucide-react';

/**
 * Operator Protocol Checklists
 * 
 * Daily/Weekly/Monthly operator checklists with completion tracking.
 * View Panel buttons change tabs AND scroll to anchors.
 */
const OperatorChecklist = ({ 
  onNavigate,  // Callback to navigate: (tab, anchor) => void
  className = "" 
}) => {
  const [checklists, setChecklists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const [error, setError] = useState(null);
  const [consistency, setConsistency] = useState(null);

  const fetchChecklists = useCallback(async () => {
    try {
      setLoading(true);
      const [checklistsRes, consistencyRes] = await Promise.all([
        api.get('/operator/checklists'),
        api.get('/operator/checklists/consistency')
      ]);
      setChecklists(checklistsRes.data);
      setConsistency(consistencyRes.data);
      // Initialize notes from server
      setEditedNotes({
        daily: checklistsRes.data.daily?.notes || '',
        weekly: checklistsRes.data.weekly?.notes || '',
        monthly: checklistsRes.data.monthly?.notes || '',
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load checklists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  const handleItemToggle = async (periodType, itemId, currentChecked) => {
    const newChecked = !currentChecked;
    
    // Optimistic update
    setChecklists(prev => {
      const updated = { ...prev };
      const checklist = { ...updated[periodType] };
      checklist.items = checklist.items.map(item => 
        item.item_id === itemId 
          ? { ...item, is_checked: newChecked } 
          : item
      );
      // Check if all items are now checked
      const allChecked = checklist.items.every(item => item.is_checked);
      checklist.completed = allChecked;
      updated[periodType] = checklist;
      return updated;
    });

    try {
      setSaving(prev => ({ ...prev, [periodType]: true }));
      const res = await api.put(`/operator/checklists/${periodType}`, {
        items: [{ item_id: itemId, is_checked: newChecked }]
      });
      // Update with server response
      setChecklists(prev => ({
        ...prev,
        [periodType]: res.data
      }));
    } catch (err) {
      // Revert on error
      fetchChecklists();
    } finally {
      setSaving(prev => ({ ...prev, [periodType]: false }));
    }
  };

  const handleSaveNotes = async (periodType) => {
    try {
      setSaving(prev => ({ ...prev, [`${periodType}_notes`]: true }));
      const res = await api.put(`/operator/checklists/${periodType}`, {
        notes: editedNotes[periodType]
      });
      setChecklists(prev => ({
        ...prev,
        [periodType]: res.data
      }));
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSaving(prev => ({ ...prev, [`${periodType}_notes`]: false }));
    }
  };

  const handleReset = async (periodType) => {
    try {
      setSaving(prev => ({ ...prev, [periodType]: true }));
      const res = await api.post(`/operator/checklists/${periodType}/reset`);
      setChecklists(prev => ({
        ...prev,
        [periodType]: res.data
      }));
      setEditedNotes(prev => ({
        ...prev,
        [periodType]: ''
      }));
    } catch (err) {
      console.error('Failed to reset checklist:', err);
    } finally {
      setSaving(prev => ({ ...prev, [periodType]: false }));
    }
  };

  const handleViewPanel = (viewTarget) => {
    if (!viewTarget || !onNavigate) return;
    
    const [tab, anchor] = viewTarget.split(':');
    onNavigate(tab, anchor);
  };

  const formatTimeAgo = (hours) => {
    if (!hours) return null;
    if (hours < 1) return `${Math.round(hours * 60)} minutes ago`;
    if (hours < 24) return `${Math.round(hours)} hours ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const getPeriodIcon = (periodType) => {
    switch (periodType) {
      case 'daily': return Clock;
      case 'weekly': return Calendar;
      case 'monthly': return CalendarDays;
      default: return Clock;
    }
  };

  const getPeriodLabel = (periodType) => {
    switch (periodType) {
      case 'daily': return 'Daily Review';
      case 'weekly': return 'Weekly Review';
      case 'monthly': return 'Monthly Review';
      default: return periodType;
    }
  };

  const getPeriodDuration = (periodType) => {
    switch (periodType) {
      case 'daily': return '24 hours';
      case 'weekly': return '7 days';
      case 'monthly': return '30 days';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`} data-testid="operator-checklist">
        {['daily', 'weekly', 'monthly'].map(period => (
          <Card key={period} className="glass border-white/10">
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="glass border-red-500/20" data-testid="operator-checklist">
        <CardContent className="pt-6">
          <p className="text-sm text-red-400">{error}</p>
          <Button onClick={fetchChecklists} className="mt-4" variant="outline" size="sm">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const renderChecklist = (periodType) => {
    const checklist = checklists?.[periodType];
    if (!checklist) return null;

    const Icon = getPeriodIcon(periodType);
    const completedCount = checklist.items?.filter(i => i.is_checked).length || 0;
    const totalCount = checklist.items?.length || 0;
    const isComplete = checklist.completed;
    const isSaving = saving[periodType];

    return (
      <Card 
        key={periodType}
        className={`glass border-white/10 ${isComplete ? 'border-emerald-500/30 bg-emerald-500/5' : ''}`}
        data-testid={`checklist-${periodType}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-200">
              <Icon className={`h-4 w-4 ${isComplete ? 'text-emerald-400' : 'text-slate-400'}`} />
              {getPeriodLabel(periodType)}
              <Badge 
                variant="outline" 
                className={`text-xs ml-2 ${
                  isComplete 
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' 
                    : 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                }`}
              >
                {completedCount}/{totalCount}
              </Badge>
            </CardTitle>
            
            <div className="flex items-center gap-2">
              {checklist.hours_since_completion !== undefined && (
                <span className="text-xs text-slate-400">
                  Last completed: {formatTimeAgo(checklist.hours_since_completion)}
                </span>
              )}
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-slate-500">
                      Resets after {getPeriodDuration(periodType)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Rolling window from last completion</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-slate-200">
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-slate-100">Reset {getPeriodLabel(periodType)}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      This will create a fresh checklist and clear all progress. 
                      The previous checklist will be saved to history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/10 border-white/10 text-slate-200 hover:bg-white/15">Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleReset(periodType)}
                      className="bg-red-500/20 text-red-300 hover:bg-red-500/30 border-red-500/30"
                    >
                      Reset
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Checklist Items */}
          <div className="space-y-2">
            {checklist.items?.map((item) => (
              <div 
                key={item.item_id}
                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                  item.is_checked 
                    ? 'bg-emerald-500/10' 
                    : 'bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`checklist-item-${item.item_id}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={item.is_checked}
                    onCheckedChange={() => handleItemToggle(periodType, item.item_id, item.is_checked)}
                    disabled={isSaving}
                    className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                  />
                  <span className={`text-sm ${item.is_checked ? 'text-slate-400 line-through' : 'text-slate-200'}`}>
                    {item.label}
                  </span>
                </div>
                
                {item.view_target && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                    onClick={() => handleViewPanel(item.view_target)}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    View
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Notes Section */}
          <div className="pt-3 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Notes
              </span>
              {editedNotes[periodType] !== (checklist.notes || '') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-indigo-400"
                  onClick={() => handleSaveNotes(periodType)}
                  disabled={saving[`${periodType}_notes`]}
                >
                  <Save className="h-3 w-3 mr-1" />
                  {saving[`${periodType}_notes`] ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
            <Textarea
              value={editedNotes[periodType] || ''}
              onChange={(e) => setEditedNotes(prev => ({ ...prev, [periodType]: e.target.value }))}
              placeholder={`Add ${periodType} notes or decisions...`}
              className="min-h-[60px] bg-white/5 border-white/10 text-slate-200 text-sm resize-none"
            />
          </div>

          {/* Completion Status */}
          {isComplete && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              <span>{getPeriodLabel(periodType)} Complete</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${className}`} data-testid="operator-checklist">
      {/* Completion Consistency Panel */}
      {consistency && (
        <Card className="glass border-white/10" data-testid="completion-consistency">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-300">
              Completion Consistency
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Daily</div>
                <div className="text-2xl font-semibold text-slate-100">
                  {consistency.daily_completed}
                </div>
                <div className="text-xs text-slate-500">last 30 days</div>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Weekly</div>
                <div className="text-2xl font-semibold text-slate-100">
                  {consistency.weekly_completed}
                </div>
                <div className="text-xs text-slate-500">last 30 days</div>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-xs text-slate-400 mb-1">Monthly</div>
                <div className="text-2xl font-semibold text-slate-100">
                  {consistency.monthly_completed}
                </div>
                <div className="text-xs text-slate-500">last 90 days</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {renderChecklist('daily')}
      {renderChecklist('weekly')}
      {renderChecklist('monthly')}
    </div>
  );
};

export default OperatorChecklist;
